/**
 * withTimeout — time-bound a pending promise so a mutate handler can never
 * hang its "Saving…" / "Creating account…" UI forever (CLAUDE.md Rule 8).
 *
 * A stalled fetch / oRPC call that never settles leaves the pending state
 * latched on with no error surfaced — the exact silent-swallow Rule 8 forbids.
 * Racing the work against a timer guarantees the handler's catch/finally runs
 * within `ms`, re-enabling retry and letting the caller surface a failure.
 *
 * The underlying promise is NOT cancelled (a plain Promise has no cancel seam);
 * callers that own an AbortController should abort it themselves. This helper is
 * for the case where the pending promise is opaque (e.g. an `onCreate` prop).
 */

/** Thrown by {@link withTimeout} when the wrapped promise does not settle in time. */
export class TimeoutError extends Error {
  override readonly name = "TimeoutError";
  constructor(
    readonly ms: number,
    label?: string,
  ) {
    super(label ? `${label} timed out after ${ms}ms` : `Operation timed out after ${ms}ms`);
  }
}

/**
 * Resolve/reject with `promise`, but reject with a {@link TimeoutError} if it
 * has not settled within `ms`. The timer is always cleared on settle so it
 * never leaks or fires after the fact.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, label?: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new TimeoutError(ms, label));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err as Error);
      },
    );
  });
}
